// apps/admin/src/features/products/category.api.ts
//
// Phase 8.2 Wave 6 Task 38 — admin-side Category API wrappers.
//
// Endpoint surface (Wave 5 Task 28 contract, per CONTEXT/294-298):
//   GET    /api/v1/admin/categories            → list categories (tree-ready)
//   POST   /api/v1/admin/categories            → create category
//   PATCH  /api/v1/admin/categories/:id        → update category
//   DELETE /api/v1/admin/categories/:id        → soft-delete (blueprint guard)
//   POST   /api/v1/admin/categories/reorder    → reorder same-level siblings
//
// Defensive shape: openapi-fetch's generated `paths` type may not yet
// include /admin/categories until the OpenAPI spec is regenerated post
// Wave 6. We cast through a LooseClient (same pattern as credit.api.ts
// + billing-shared.ts) until codegen catches up.
//
// All mutations carry Idempotency-Key convention.

import { apiClient } from '@printway/shared/api';

// ---------------------------------------------------------------------------
// Shared envelope + idempotency helpers — local mirror of billing-shared
// (decoupled so the products feature doesn't pull billing imports).
// ---------------------------------------------------------------------------

interface SuccessEnvelope<T> {
  success: true;
  data: T;
  requestId: string;
}

interface ErrorEnvelope {
  success: false;
  error: { code: string; message: string; details?: Record<string, unknown> };
  requestId: string;
}

interface LooseClient {
  GET: (
    path: string,
    init?: { headers?: Record<string, string>; params?: unknown },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
  POST: (
    path: string,
    init?: { headers?: Record<string, string>; body?: unknown; params?: unknown },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
  PATCH: (
    path: string,
    init?: { headers?: Record<string, string>; body?: unknown; params?: unknown },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
  DELETE: (
    path: string,
    init?: { headers?: Record<string, string>; params?: unknown },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
}

function unwrap<T>(payload: unknown): T {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as SuccessEnvelope<T>).success === true &&
    'data' in payload
  ) {
    return (payload as SuccessEnvelope<T>).data;
  }
  return payload as T;
}

function envelopeError(payload: unknown): {
  code: string | null;
  message: string | null;
} {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as ErrorEnvelope).success === false &&
    'error' in payload
  ) {
    const err = (payload as ErrorEnvelope).error;
    return { code: err?.code ?? null, message: err?.message ?? null };
  }
  return { code: null, message: null };
}

function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `idemp-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

// ---------------------------------------------------------------------------
// Wire types — mirror category.admin.handler.ts response shapes.
// ---------------------------------------------------------------------------

export type CategoryStatus = 'Active' | 'Inactive';

export interface CategoryI18nName {
  en: string;
  vi?: string;
  fr?: string;
}

export interface CategorySEO {
  title: string;
  canonicalUrl?: string | null;
  googleIndex: boolean;
  metaKeywords?: string | null;
  metaDescription: string;
}

export interface CategoryRow {
  id: string;
  name: CategoryI18nName;
  slug: string;
  parentId: string | null;
  sortOrder: number;
  status: CategoryStatus;
  /**
   * Homepage visibility, independent of `status` (Category.md BR-5/BR-6).
   * Optional on the wire so a response from a pre-Batch-1 API still parses;
   * readers treat `undefined` as `true`.
   */
  display?: boolean;
  alias?: string | null;
  shortDescription?: string | null;
  blueprintIds: string[];
  image?: string | null;
  seo: CategorySEO;
  createdAt?: number;
  updatedAt?: number;
  updatedBy?: string | null;
  /** Email of the last editor — BR-7. Empty when the actor is unknown. */
  updatedByEmail?: string | null;
  /** Direct children — the form uses it to lock the Parent picker. */
  childCount?: number;
}

// BE returns the sibling-ordered flat array directly inside the envelope's
// `data` field (apps/api/src/modules/catalog/categories/category.admin.service.ts
// listAdminCategories → AdminCategoryItem[]). `unwrap()` strips the envelope,
// so callers receive CategoryRow[] — there is no wrapping {rows,total} object.
export type ListCategoriesResponse = CategoryRow[];

export interface CreateCategoryInput {
  name: CategoryI18nName;
  /**
   * Ignored by the server — the slug is always derived from `name.en`
   * (Category.md: auto-generated, not user-editable). Kept on the type so
   * older callers still compile.
   */
  slug?: string;
  parentId?: string | null;
  sortOrder?: number;
  status?: CategoryStatus;
  display?: boolean;
  alias?: string | null;
  shortDescription?: string | null;
  blueprintIds?: string[];
  image?: string | null;
  seo?: Partial<CategorySEO>;
}

export type UpdateCategoryInput = Partial<CreateCategoryInput>;

export interface ReorderCategoriesInput {
  items: Array<{ id: string; sortOrder: number }>;
}

export interface ReorderCategoriesResponse {
  reordered: number;
}

export interface DeleteCategoryResponse {
  id: string;
  deleted: true;
}

export class CategoryApiError extends Error {
  readonly code: string;
  readonly status: number;
  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = 'CategoryApiError';
    this.code = code;
    this.status = status;
  }
}

function throwApi(err: unknown, response: Response, fallback: string): never {
  const { code, message } = envelopeError(err);
  throw new CategoryApiError(
    code ?? 'NETWORK_ERROR',
    message ?? fallback,
    response.status,
  );
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export interface ListCategoriesFilters {
  status?: CategoryStatus;
  parentId?: string | 'null';
  topLevelOnly?: boolean;
}

/** Filters for the paged admin list table (Batch 1 BE contract). */
export interface ListCategoriesPagedFilters {
  status?: CategoryStatus;
  display?: boolean;
  search?: string;
  page: number;
  pageSize: number;
}

export interface ListCategoriesPagedResponse {
  items: CategoryRow[];
  /** Total rows across all pages — parents AND children. */
  total: number;
  page: number;
  pageSize: number;
}

/**
 * GET /api/v1/admin/categories?page=… — paged, tree-ordered rows.
 *
 * Sending `page`/`pageSize` switches the endpoint to the envelope shape;
 * without them it still returns a bare array (see listAdminCategories).
 */
export async function listAdminCategoriesPaged(
  filters: ListCategoriesPagedFilters,
): Promise<ListCategoriesPagedResponse> {
  const c = apiClient() as unknown as LooseClient;
  const qs = new URLSearchParams();
  qs.set('page', String(filters.page));
  qs.set('pageSize', String(filters.pageSize));
  if (filters.status) qs.set('status', filters.status);
  if (filters.display !== undefined) qs.set('display', String(filters.display));
  const search = filters.search?.trim();
  if (search) qs.set('search', search);

  const { data, error, response } = await c.GET(
    `/api/v1/admin/categories?${qs.toString()}`,
  );
  if (error || !response.ok) {
    throwApi(error ?? data, response, 'Failed to load categories');
  }
  return unwrap<ListCategoriesPagedResponse>(data);
}

/** GET /api/v1/admin/categories — returns the full sibling-ordered list. */
export async function listAdminCategories(
  filters: ListCategoriesFilters = {},
): Promise<ListCategoriesResponse> {
  const c = apiClient() as unknown as LooseClient;
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.parentId !== undefined) qs.set('parentId', filters.parentId);
  if (filters.topLevelOnly) qs.set('topLevelOnly', 'true');
  const query = qs.toString();
  const path =
    '/api/v1/admin/categories' + (query.length > 0 ? `?${query}` : '');
  const { data, error, response } = await c.GET(path);
  if (error || !response.ok) {
    throwApi(error ?? data, response, 'Failed to load categories');
  }
  return unwrap<ListCategoriesResponse>(data);
}

/** GET /api/v1/admin/categories/:id — single row for the Edit page. */
export async function getAdminCategory(id: string): Promise<CategoryRow> {
  const c = apiClient() as unknown as LooseClient;
  const { data, error, response } = await c.GET(
    `/api/v1/admin/categories/${encodeURIComponent(id)}`,
  );
  if (error || !response.ok) {
    throwApi(error ?? data, response, 'Failed to load category');
  }
  return unwrap<CategoryRow>(data);
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/** POST /api/v1/admin/categories. */
export async function createAdminCategory(
  dto: CreateCategoryInput,
): Promise<CategoryRow> {
  const c = apiClient() as unknown as LooseClient;
  const { data, error, response } = await c.POST(
    '/api/v1/admin/categories',
    {
      headers: { 'Idempotency-Key': newIdempotencyKey() },
      body: dto,
    },
  );
  if (error || !response.ok) {
    throwApi(error ?? data, response, 'Failed to create category');
  }
  return unwrap<CategoryRow>(data);
}

/** PATCH /api/v1/admin/categories/:id. */
export async function updateAdminCategory(
  id: string,
  dto: UpdateCategoryInput,
): Promise<CategoryRow> {
  const c = apiClient() as unknown as LooseClient;
  const path = `/api/v1/admin/categories/${encodeURIComponent(id)}`;
  const { data, error, response } = await c.PATCH(path, {
    headers: { 'Idempotency-Key': newIdempotencyKey() },
    body: dto,
  });
  if (error || !response.ok) {
    throwApi(error ?? data, response, 'Failed to update category');
  }
  return unwrap<CategoryRow>(data);
}

/**
 * DELETE /api/v1/admin/categories/:id. Soft-delete; server returns 409
 * CATEGORY_HAS_BLUEPRINTS if any blueprint still references this category
 * .
 */
export async function deleteAdminCategory(
  id: string,
): Promise<DeleteCategoryResponse> {
  const c = apiClient() as unknown as LooseClient;
  const path = `/api/v1/admin/categories/${encodeURIComponent(id)}`;
  const { data, error, response } = await c.DELETE(path, {
    headers: { 'Idempotency-Key': newIdempotencyKey() },
  });
  if (error || !response.ok) {
    throwApi(error ?? data, response, 'Failed to delete category');
  }
  return unwrap<DeleteCategoryResponse>(data);
}

/**
 * POST /api/v1/admin/categories/reorder. Caller MUST pass only same-level
 * siblings — server enforces (returns 409 CATEGORY_REORDER_CROSS_LEVEL
 * otherwise).
 */
export async function reorderAdminCategories(
  input: ReorderCategoriesInput,
): Promise<ReorderCategoriesResponse> {
  const c = apiClient() as unknown as LooseClient;
  const { data, error, response } = await c.POST(
    '/api/v1/admin/categories/reorder',
    {
      headers: { 'Idempotency-Key': newIdempotencyKey() },
      body: input,
    },
  );
  if (error || !response.ok) {
    throwApi(error ?? data, response, 'Failed to reorder categories');
  }
  return unwrap<ReorderCategoriesResponse>(data);
}

// ---------------------------------------------------------------------------
// TanStack Query keys — co-located so the page + tree + detail panel share
// invalidation surface.
// ---------------------------------------------------------------------------

export const categoryQueryKeys = {
  all: ['admin', 'categories'] as const,
  list: (filters: ListCategoriesFilters = {}) =>
    ['admin', 'categories', 'list', filters] as const,
  paged: (filters: ListCategoriesPagedFilters) =>
    ['admin', 'categories', 'paged', filters] as const,
  detail: (id: string) => ['admin', 'categories', 'detail', id] as const,
};
